// src/components/upmode-agent/upmode-agent-page.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight, MessageSquare, Plus, Trash2, Settings, Globe, Rocket,
  PanelLeft, X, Shield, Sparkles, PenLine,
} from "lucide-react";
import { toast } from "sonner";
import {
  upmodeAgentApi, streamChat,
  UpmodeAgent, UpmodeConversationSummary, UpmodeMessage,
} from "@/app/services/upmode-agent/upmode-agent-api";
import { ChatMessage } from "./chat-message";
import { EmailConfirmCard, PendingEmail } from "./email-confirm-card";
import { PublishConfirmCard, PendingPublish } from "./publish-confirm-card";
import { AgentManagerModal } from "./agent-manager-modal";
import { ConfirmDialog } from "@/app/components/shared/confirm-dialog";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import {
  Banner, Button, EmptyState, Icon, IconButton, SearchInput, Spinner,
} from "../kit";
import {
  CapabilityPill, Orb, OrbButton, ToolStep, agentTone, capabilitiesOf,
} from "@/app/components/shared/advanced-vocabulary";
import "@/styles/advanced-tier.css";
import "@/styles/agent-page.css";

interface UpmodeAgentPageProps {
  activeBrandName?: string;
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (c: boolean) => void;
}

export function UpmodeAgentPage({ activeBrandName }: UpmodeAgentPageProps) {
  const [agents, setAgents] = useState<UpmodeAgent[]>([]);
  const [conversations, setConversations] = useState<UpmodeConversationSummary[]>([]);
  const [activeConvId, setActiveConvId] = useState<number | null>(null);
  const [activeConv, setActiveConv] = useState<UpmodeConversationSummary | null>(null);
  const [messages, setMessages] = useState<UpmodeMessage[]>([]);
  const [streamingText, setStreamingText] = useState("");
  const [toolEvents, setToolEvents] = useState<
    Array<{ type: "tool_start" | "tool_result"; name: string; id: string; result?: any }>
  >([]);
  const [pendingEmail, setPendingEmail] = useState<PendingEmail | null>(null);

  // NEW: pending publish confirmation card
  const [pendingPublish, setPendingPublish] = useState<PendingPublish | null>(null);
  const [publishing, setPublishing] = useState(false);

  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAgentManager, setShowAgentManager] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [convPendingDelete, setConvPendingDelete] = useState<UpmodeConversationSummary | null>(null);

  const { isDesktop } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ── Load data ────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setIsLoading(true); setError(null);
    try {
      const [agentList, convList] = await Promise.all([
        upmodeAgentApi.listAgents(activeBrandName),
        upmodeAgentApi.listConversations(100),
      ]);
      setAgents(agentList);
      setConversations(convList);
    } catch (e: any) {
      setError(e.message || "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [activeBrandName]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!activeConvId) {
      setMessages([]); setActiveConv(null); return;
    }
    upmodeAgentApi.getConversation(activeConvId).then((detail) => {
      setActiveConv(detail.conversation);
      setMessages(detail.messages);
      setStreamingText("");
      setToolEvents([]);
      setPendingEmail(null);
      setPendingPublish(null);
    }).catch((e) => toast.error(e.message));
  }, [activeConvId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText, toolEvents, pendingEmail, pendingPublish]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }, [input]);

  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // ── Start a new conversation ─────────────────────────────────────────────

  const startConversation = async (agentId: number | null) => {
    setDrawerOpen(false);
    try {
      const agent = agentId ? agents.find(a => a.id === agentId) ?? null : null;
      const conv = await upmodeAgentApi.createConversation({
        agent_id: agentId,
        brand_name: activeBrandName || null,
        title: "New conversation",
      });
      setConversations([conv, ...conversations]);
      setActiveConvId(conv.id);
      if (agent) toast.success(`Started chat with ${agent.name}`);
    } catch (e: any) {
      toast.error(e.message || "Failed to create conversation");
    }
  };

  const requestDeleteConversation = (conv: UpmodeConversationSummary) => {
    setConvPendingDelete(conv);
  };

  const confirmDeleteConversation = async () => {
    const conv = convPendingDelete;
    if (!conv) return;
    try {
      await upmodeAgentApi.deleteConversation(conv.id);
      setConversations(prev => prev.filter(c => c.id !== conv.id));
      if (activeConvId === conv.id) setActiveConvId(null);
      toast.success("Conversation deleted");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setConvPendingDelete(null);
    }
  };

  // ── Send message ─────────────────────────────────────────────────────────

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !activeConvId || isStreaming) return;

    setInput("");
    setIsStreaming(true);
    setStreamingText("");
    setToolEvents([]);
    setPendingEmail(null);
    setPendingPublish(null);

    const optimistic: UpmodeMessage = {
      id: -Date.now(),
      role: "user",
      content: text,
      tool_calls: [], tool_results: [], metadata: {},
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);

    const controller = new AbortController();
    abortRef.current = controller;

    let assistantText = "";
    const localToolEvents: typeof toolEvents = [];

    try {
      await streamChat(
        activeConvId,
        text,
        (evt) => {
          if (evt.type === "text") {
            assistantText += evt.delta;
            setStreamingText(assistantText);
          } else if (evt.type === "tool_use_start") {
            localToolEvents.push({ type: "tool_start", name: evt.name, id: evt.id });
            setToolEvents([...localToolEvents]);
          } else if (evt.type === "tool_result") {
            localToolEvents.push({ type: "tool_result", name: evt.name, id: evt.id, result: evt.result });
            setToolEvents([...localToolEvents]);
          } else if (evt.type === "email_confirmation") {
            setPendingEmail({
              tool_use_id: evt.tool_use_id,
              to_email: evt.to_email,
              subject: evt.subject,
              body: evt.body,
            });
          } else if (evt.type === "publish_confirmation") {
            // NEW — agent asked to publish, render the approval card
            setPendingPublish({
              tool_use_id: evt.tool_use_id,
              destinations: evt.destinations,
              content: evt.content,
              summary: evt.summary,
            });
          } else if (evt.type === "error") {
            toast.error(evt.message);
          }
        },
        controller.signal,
      );

      const detail = await upmodeAgentApi.getConversation(activeConvId);
      setMessages(detail.messages);
      setStreamingText("");
      setToolEvents([]);

      upmodeAgentApi.listConversations(100).then(setConversations).catch(() => {});
    } catch (e: any) {
      if (e.name !== "AbortError") {
        toast.error(e.message || "Chat failed");
        setError(e.message || "Chat failed");
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  };

  const handleConfirmEmail = async (email: PendingEmail, edited?: Partial<PendingEmail>) => {
    if (!activeConvId) return;
    const payload = {
      conversation_id: activeConvId,
      to_email: edited?.to_email ?? email.to_email,
      subject: edited?.subject ?? email.subject,
      body: edited?.body ?? email.body,
    };
    try {
      const res = await upmodeAgentApi.sendEmail(payload);
      if (res.status === "sent") {
        toast.success(`Email sent to ${payload.to_email}`);
      } else if (res.status === "not_configured") {
        toast.warning("SMTP is not configured — email not sent.");
      } else {
        toast.error(`Email failed: ${res.error || res.status}`);
      }
      setPendingEmail(null);
      const detail = await upmodeAgentApi.getConversation(activeConvId);
      setMessages(detail.messages);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleCancelEmail = () => {
    setPendingEmail(null);
    toast.info("Email cancelled");
  };

  // ── NEW: publish confirmation handlers ─────────────────────────────────

  const handleConfirmPublish = async (
    selected: Array<{ destination: any; content: Record<string, any> }>,
  ) => {
    if (!activeConvId || !pendingPublish) return;
    setPublishing(true);
    try {
      const res = await upmodeAgentApi.confirmPublish({
        conversation_id: activeConvId,
        tool_use_id: pendingPublish.tool_use_id,
        destinations: selected,
      });

      const publishedCount = res.results.filter(r => r.status === "published").length;
      const failedCount    = res.results.filter(r => r.status !== "published").length;

      if (res.status === "published") {
        toast.success(`Published to ${publishedCount} destination${publishedCount === 1 ? "" : "s"}`);
      } else if (res.status === "partially_published") {
        toast.warning(`Published to ${publishedCount}, ${failedCount} failed`);
      } else {
        toast.error("Publishing failed for all destinations");
      }

      setPendingPublish(null);
      const detail = await upmodeAgentApi.getConversation(activeConvId);
      setMessages(detail.messages);
    } catch (e: any) {
      toast.error(e.message || "Publishing failed");
    } finally {
      setPublishing(false);
    }
  };

  const handleCancelPublish = () => {
    setPendingPublish(null);
    toast.info("Publish cancelled");
  };

  const filteredConvs = useMemo(() => {
    if (!searchQuery) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter(c =>
      c.title.toLowerCase().includes(q) ||
      (c.brand_name || "").toLowerCase().includes(q)
    );
  }, [conversations, searchQuery]);

  const activeAgent = activeConv?.agent_id ? agents.find(a => a.id === activeConv.agent_id) ?? null : null;
  const hasWebSearch = !!activeAgent?.tools_enabled?.web_search;
  const hasPublish = !!(activeAgent?.publish_destinations && activeAgent.publish_destinations.length > 0);

  /* ── conversation grouping ───────────────────────────────────────────────
     The rail groups by recency off `updated_at`, which is a field that was
     already on the summary and was already being ignored. Grouping is derived
     rather than stored, so a conversation that gets a reply moves group on the
     next load without anything being written. */
  const convGroups = useMemo(() => {
    const now = Date.now();
    const DAY = 86400000;
    const order = ["Today", "Yesterday", "Earlier this week", "Earlier"];
    const bucket = (iso?: string | null) => {
      const t = iso ? new Date(iso).getTime() : NaN;
      if (!Number.isFinite(t)) return "Earlier";
      const age = now - t;
      if (age < DAY) return "Today";
      if (age < DAY * 2) return "Yesterday";
      if (age < DAY * 7) return "Earlier this week";
      return "Earlier";
    };
    const by = new Map<string, UpmodeConversationSummary[]>();
    for (const c of filteredConvs) {
      const key = bucket((c as { updated_at?: string }).updated_at ?? c.created_at);
      const list = by.get(key);
      if (list) list.push(c);
      else by.set(key, [c]);
    }
    return order.filter((k) => by.has(k)).map((k) => ({ label: k, items: by.get(k)! }));
  }, [filteredConvs]);

  // ── Rail ─────────────────────────────────────────────────────────────────

  const railHeader = (
    <>
      <div className="upm-agent__railhead">
        <Orb size="md" />
        <div className="upm-agent__mark">
          <div className="upm-agent__markname">Upmode Agent</div>
          <div className="upm-agent__marksub">
            {activeBrandName ? `${activeBrandName} · reasons over your data` : "Reasons over your data"}
          </div>
        </div>
        <IconButton
          icon={Settings}
          label="Manage agents"
          onClick={() => { setShowAgentManager(true); setDrawerOpen(false); }}
        />
        {!isDesktop && (
          <IconButton icon={X} label="Close agents menu" onClick={() => setDrawerOpen(false)} />
        )}
      </div>

      {/* ⚠ flex-shrink: 1 with its own scroller. At flex-shrink: 0 — which is
          what shipped — four agent cards take the column and crush the
          conversations list below to a sliver. */}
      <div className="upm-agent__agents">
        <p className="upm-adv-eyebrow" style={{ marginBottom: "var(--sp-2)" }}>Start with</p>
        <div className="upm-agent__agents-scroll">
          {agents.map((a) => {
            // Only the DIFFERENTIATING capabilities on a rail card: every agent
            // reads Upmode data, so that pill says nothing here. The thread
            // header carries the full set.
            const caps = capabilitiesOf(a, { differentiatingOnly: true });
            const tone = agentTone(capabilitiesOf(a));
            return (
              <button key={a.id} className="upm-agent__card" type="button" onClick={() => startConversation(a.id)}>
                {/* The icon chip replaces `avatar_emoji`. The FIELD is
                    untouched — it is simply not rendered, per §0.5. */}
                <span className={`upm-agent__chip upm-agent__chip--${tone}`}>
                  <Icon icon={tone === "publish" ? Rocket : tone === "web" ? Globe : PenLine} size="sm" />
                </span>
                <span className="upm-agent__cardbody">
                  <span className="upm-agent__cardname">{a.name}</span>
                  <span className="upm-agent__carddesc" style={{ display: "block" }}>
                    {a.description}
                  </span>
                  {caps.length > 0 && (
                    <span className="upm-agent__caps">
                      {caps.map((c) => <CapabilityPill key={c} cap={c} />)}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            className="upm-agent__card upm-agent__card--dashed"
            onClick={() => { setShowAgentManager(true); setDrawerOpen(false); }}
          >
            <Icon icon={Plus} size="sm" />
            Create custom agent
          </button>
        </div>
      </div>

      <div className="upm-agent__convhead">
        <div className="upm-agent__convhead-row">
          <span className="upm-adv-eyebrow">Conversations</span>
        </div>
        <SearchInput
          value={searchQuery}
          placeholder="Search conversations…"
          aria-label="Search conversations"
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
        />
      </div>
    </>
  );

  const railList = (
    <div className="upm-agent__convlist">
      {isLoading && <Spinner label="Loading conversations" />}
      {!isLoading && filteredConvs.length === 0 && (
        <p className="upm-agent__convmeta" style={{ padding: "var(--sp-6) var(--sp-3)", whiteSpace: "normal" }}>
          {searchQuery ? "No conversations match your search." : "No conversations yet. Pick an agent above to start."}
        </p>
      )}
      {convGroups.map((g) => (
        <div className="upm-agent__group" key={g.label}>
          <div className="upm-agent__grouplabel">{g.label}</div>
          {g.items.map((c) => (
            <div
              key={c.id}
              className="upm-agent__conv"
              role="button"
              tabIndex={0}
              aria-current={activeConvId === c.id}
              onClick={() => { setActiveConvId(c.id); setDrawerOpen(false); }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                setActiveConvId(c.id);
                setDrawerOpen(false);
              }}
            >
              <span className="upm-agent__convicon">
                <Icon icon={MessageSquare} size="sm" />
              </span>
              <div className="upm-agent__convmain">
                <div className="upm-agent__convtitle">{c.title}</div>
                <div className="upm-agent__convmeta">
                  {c.agent_name}
                  {c.brand_name ? ` · ${c.brand_name}` : ""}
                </div>
              </div>
              <span className="upm-agent__convdel">
                <IconButton
                  icon={Trash2}
                  label={`Delete ${c.title}`}
                  onClick={(e) => { e.stopPropagation(); requestDeleteConversation(c); }}
                />
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const rail = <>{railHeader}{railList}</>;

  /* The scope line. Three copy variants, driven by the same two flags they
     always were — what changed is that they no longer come in three different
     tinted bands, which read as three severities of warning for three states
     that are all normal. */
  const scopeLine = hasPublish ? (
    <>
      Reasons over your Upmode data{hasWebSearch ? " and the live web" : ""}, and can publish to{" "}
      {(activeAgent?.publish_destinations || []).join(", ")}. Nothing is published without your approval.
    </>
  ) : hasWebSearch ? (
    <>Reasons over your Upmode data and the live web. Upmode data wins on brand questions.</>
  ) : (
    <>Reasons only over your Upmode data. Enable web search or publishing on a custom agent to unlock more.</>
  );

  const threadCaps = capabilitiesOf(activeAgent);

  return (
    <>
      <div
        data-kit
        className="upm-agent"
        style={{ gridTemplateColumns: isDesktop ? "var(--w-rail) 1fr" : "1fr" }}
      >
        {isDesktop ? (
          <div className="upm-agent__rail">{rail}</div>
        ) : (
          <>
            {drawerOpen && <div className="upm-agent__scrim" onClick={() => setDrawerOpen(false)} />}
            <div className="upm-agent__drawer" data-open={drawerOpen} aria-hidden={!drawerOpen}>
              {rail}
            </div>
          </>
        )}

        <div className="upm-agent__chat">
          {!isDesktop && (
            <div className="upm-agent__mobilebar">
              <IconButton icon={PanelLeft} label="Agents and conversations" onClick={() => setDrawerOpen(true)} />
              {activeConvId ? (
                <>
                  <Orb size="sm" />
                  <div className="upm-agent__mark">
                    <div className="upm-agent__convtitle">{activeConv?.title}</div>
                    <div className="upm-agent__convmeta">
                      {activeAgent?.name || activeConv?.agent_name}
                      {activeConv?.brand_name ? ` · ${activeConv.brand_name}` : ""}
                    </div>
                  </div>
                </>
              ) : (
                <div className="upm-agent__mark">
                  <div className="upm-agent__markname">Upmode Agent</div>
                  <div className="upm-agent__marksub">
                    {activeBrandName ? `${activeBrandName} · reasons over your data` : "Reasons over your data"}
                  </div>
                </div>
              )}
            </div>
          )}

          {!activeConvId ? (
            <div className="upm-agent__empty">
              <EmptyState
                icon={Sparkles}
                title="Start a conversation"
                description={
                  isDesktop
                    ? "Pick an agent from the rail, or create a custom one. Agents reason over your Upmode data by default; web search and publishing are per-agent capabilities."
                    : "Open the rail to pick an agent, or create a custom one. Agents reason over your Upmode data by default."
                }
                action={
                  !isDesktop ? (
                    <Button variant="secondary" icon={PanelLeft} onClick={() => setDrawerOpen(true)}>
                      Browse agents
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <>
              {isDesktop && (
                <div className="upm-agent__threadhead">
                  <Orb size="md" />
                  <div className="upm-agent__mark">
                    <div className="upm-agent__threadtitle">{activeConv?.title}</div>
                    <div className="upm-agent__threadmeta">
                      {activeAgent?.name || activeConv?.agent_name}
                      {activeConv?.brand_name ? ` · ${activeConv.brand_name}` : ""}
                    </div>
                  </div>
                  <div className="upm-agent__threadcaps">
                    {threadCaps.map((c) => <CapabilityPill key={c} cap={c} large />)}
                  </div>
                </div>
              )}

              <div className="upm-agent__scope">
                <Icon icon={Shield} size="sm" />
                <span>{scopeLine}</span>
              </div>

              <div ref={scrollRef} className="upm-agent__messages">
                {messages.filter(m => m.role !== "tool").map(m => (
                  <ChatMessage key={m.id} message={m} />
                ))}

                {toolEvents.length > 0 && (
                  <div className="upm-msg upm-msg--agent">
                    <Orb size="xs" />
                    <div className="upm-msg__body">
                      <div className="upm-toolsteps">
                        {toolEvents.map((ev, i) => (
                          <ToolStep
                            key={i}
                            running={ev.type === "tool_start"}
                            label={
                              ev.type === "tool_start"
                                ? <>Calling <strong>{ev.name}</strong></>
                                : <>Got a result from <strong>{ev.name}</strong></>
                            }
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {streamingText && (
                  <ChatMessage
                    message={{
                      id: -1, role: "assistant", content: streamingText,
                      tool_calls: [], tool_results: [], metadata: {},
                      created_at: new Date().toISOString(),
                    }}
                    streaming
                  />
                )}

                {pendingEmail && (
                  <EmailConfirmCard
                    email={pendingEmail}
                    onConfirm={(edited) => handleConfirmEmail(pendingEmail, edited)}
                    onCancel={handleCancelEmail}
                  />
                )}

                {pendingPublish && (
                  <PublishConfirmCard
                    pending={pendingPublish}
                    onConfirm={handleConfirmPublish}
                    onCancel={handleCancelPublish}
                    busy={publishing}
                  />
                )}

                {isStreaming && !streamingText && toolEvents.length === 0 && (
                  <div className="upm-msg upm-msg--agent">
                    <Orb size="xs" />
                    <div className="upm-msg__body">
                      <div className="upm-toolsteps">
                        <ToolStep running label="Thinking" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="upm-agent__composer">
                <div className="upm-promptbar">
                  <textarea
                    ref={inputRef}
                    className="upm-promptbar__input"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder={
                      isStreaming
                        ? "Generating…"
                        : `Ask ${activeAgent?.name || "the agent"} anything about ${activeBrandName || "your brand"}…`
                    }
                    rows={1}
                    aria-label="Message the agent"
                    disabled={isStreaming || !!pendingEmail || !!pendingPublish}
                  />
                  <span className="upm-promptbar__hint" aria-hidden="true">↵</span>
                  <OrbButton
                    size="lg"
                    icon={ArrowRight}
                    label="Send message"
                    spinning={isStreaming}
                    onClick={handleSend}
                    disabled={!input.trim() || isStreaming || !!pendingEmail || !!pendingPublish}
                  />
                </div>

                {error && <div className="mt-2"><Banner tone="error">{error}</Banner></div>}
              </div>
            </>
          )}
        </div>
      </div>

      {showAgentManager && (
        <AgentManagerModal
          agents={agents}
          activeBrandName={activeBrandName}
          onClose={() => setShowAgentManager(false)}
          onChanged={loadAll}
        />
      )}

      <ConfirmDialog
        open={convPendingDelete !== null}
        onOpenChange={(open) => { if (!open) setConvPendingDelete(null); }}
        onConfirm={confirmDeleteConversation}
        title="Delete this conversation?"
        description={
          convPendingDelete
            ? <>This will permanently remove <strong>"{convPendingDelete.title}"</strong> and its message history. This cannot be undone.</>
            : undefined
        }
        confirmLabel="Delete"
        destructive
      />
    </>
  );
}